import React, { useState, useEffect, forwardRef, useId } from 'react'
import type { FieldError as HookFieldError } from 'react-hook-form'

import { cn } from '@/lib/utils'
import Icon, { IconTypes } from '@/components/Icon'
import Image from '@/components/Image'
import Button from '@/components/livecontact/Button'
import FieldError from '@/components/livecontact/FieldError'
import LcIcon from '@/components/livecontact/icons'
import { INPUT_ERROR_CLASS, LABEL_CLASS, NOTE_CLASS } from '@/components/livecontact/fieldStyles'

type FileUploaderProps = {
  value: File | string | null
  onChange: (file: File | null) => void
  accept?: string
  label?: string
  disabled?: boolean
  className?: string
  classInput?: string
  classLabel?: string
  classLabelInner?: string
  note?: string
  iconUpload?: IconTypes
  btnText?: string
  error?: HookFieldError
  classInnerInput?: string
  classImg?: string
  variant?: 'default' | 'smallImg' | 'row' | 'banner'
  acceptHint?: string
  /** `banner` variant: shown (dimmed as "Default") while the field is empty. */
  fallbackPreview?: string
}

/**
 * File picker in four shapes: `default` drop zone, `smallImg` avatar-style preview + button,
 * `row` (a single 32px field-like row) and `banner` (a wide hero-ratio preview). Same value
 * contract in all of them.
 */
const FileField = forwardRef<HTMLInputElement, FileUploaderProps>(
  (
    {
      value,
      onChange,
      accept = '*/*',
      className,
      classInput,
      classLabel,
      classLabelInner,
      error,
      note,
      disabled,
      classImg,
      btnText,
      iconUpload = 'upload',
      label = 'Upload File',
      classInnerInput,
      variant = 'default',
      acceptHint,
      fallbackPreview,
    },
    ref
  ) => {
    const [preview, setPreview] = useState<string | null>(null)
    const [isImage, setIsImage] = useState(false)
    const inputId = useId()

    useEffect(() => {
      return () => {
        if (value instanceof File && preview) URL.revokeObjectURL(preview)
      }
    }, [value, preview])

    useEffect(() => {
      if (value instanceof File) {
        const imageType = value.type.startsWith('image/')
        setIsImage(imageType)
        setPreview(imageType ? URL.createObjectURL(value) : null)
      } else if (typeof value === 'string') {
        const imageType =
          value.match(/\.(jpeg|jpg|gif|png|webp|svg)$/i) !== null || value.startsWith('blob:')
        setIsImage(imageType)
        setPreview(value)
      } else {
        setIsImage(false)
        setPreview(null)
      }
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0] || null
      onChange(selectedFile)
      e.target.value = ''
    }

    const handleRemove = () => onChange(null)

    const labelNode = label ? (
      <div className={cn(LABEL_CLASS, classLabelInner)}>
        <label htmlFor={inputId} className={classLabel}>
          {label}
        </label>
      </div>
    ) : null

    // ── Small image variant ────────────────────────────────────────────────
    if (variant === 'smallImg') {
      return (
        <div className={cn('relative', className)}>
          {labelNode}
          <div className="flex items-center gap-4">
            <div className="relative size-16 shrink-0">
              {preview ? (
                <div className="relative size-full overflow-hidden rounded-[12px] bg-canvas shadow-tab">
                  <Image
                    src={preview}
                    alt="Preview"
                    width={64}
                    height={64}
                    className="size-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={handleRemove}
                    aria-label="Remove image"
                    className="absolute right-1 top-1 inline-flex size-5 items-center justify-center rounded-full bg-ink/70 text-white transition-colors hover:bg-[#D33]"
                  >
                    <LcIcon name="close" size={12} />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor={inputId}
                  className="flex size-full cursor-pointer items-center justify-center rounded-[12px] border border-dashed border-line-2 bg-canvas text-ink-4 transition-colors hover:border-brand hover:text-brand dark:bg-surface-2"
                >
                  <Icon className="size-6 fill-current" name={iconUpload || 'image'} />
                </label>
              )}
            </div>

            <div className="flex min-w-0 flex-col gap-1.5">
              <input
                id={inputId}
                type="file"
                accept={accept}
                onChange={handleChange}
                className="hidden"
                disabled={disabled}
                ref={ref}
              />
              <Button asChild variant="secondary" className="w-fit cursor-pointer">
                <label htmlFor={inputId}>
                  <Icon className="mr-2 size-4 fill-current" name={iconUpload} />
                  {btnText || 'Upload image'}
                </label>
              </Button>
              {note && (
                <p className="text-[12px] leading-[1.4] tracking-[-0.2px] text-ink-4">{note}</p>
              )}
            </div>
          </div>
          <FieldError message={error?.message} />
        </div>
      )
    }

    // ── Banner variant ─────────────────────────────────────────────────────
    if (variant === 'banner') {
      const shownPreview = preview || fallbackPreview
      const isFallback = !preview && Boolean(fallbackPreview)

      return (
        <div className={cn('relative', className)}>
          {labelNode}

          <div className="flex flex-col gap-2">
            <label
              htmlFor={inputId}
              className={cn(
                'group/banner relative block aspect-[1440/372] w-full cursor-pointer overflow-hidden rounded-[12px] bg-canvas outline-none transition-shadow duration-[160ms] ease-out-strong dark:bg-surface-2',
                shownPreview
                  ? 'shadow-tab hover:shadow-[0_4px_4px_-3px_rgba(0,0,0,0.1),0_0_0_1px_rgb(var(--lc-line-2))]'
                  : 'border border-dashed border-line-2 hover:border-brand/60',
                error?.message && 'border border-[#D33]',
                classInnerInput
              )}
            >
              {shownPreview ? (
                <>
                  {/* Plain <img>: the value is often a blob: object URL, which next/image
                      cannot optimise. */}
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={shownPreview}
                    alt={isFallback ? 'Default banner' : 'Banner preview'}
                    className={cn('size-full object-cover animate-lc-pop', classImg)}
                  />
                  {/* Scrim + label only on hover / keyboard focus — the banner itself is the subject. */}
                  <span
                    aria-hidden
                    className="absolute inset-0 flex items-center justify-center bg-ink/35 opacity-0 transition-opacity duration-[160ms] ease-out-strong group-hover/banner:opacity-100 group-focus-within/banner:opacity-100"
                  >
                    <span className="inline-flex h-8 items-center gap-1.5 rounded-[9px] bg-surface/95 px-3 text-[13px] font-medium tracking-[-0.2px] text-ink shadow-input">
                      <LcIcon name="upload-small" size={12} />
                      {btnText || (isFallback ? 'Upload banner' : 'Replace banner')}
                    </span>
                  </span>
                  {isFallback && (
                    <span className="pointer-events-none absolute left-2 top-2 inline-flex h-5 items-center rounded-full bg-surface/85 px-2 text-[11px] font-medium leading-none tracking-[-0.2px] text-ink-3 backdrop-blur transition-opacity duration-[160ms] group-hover/banner:opacity-0">
                      Default
                    </span>
                  )}
                </>
              ) : (
                <span className="flex size-full flex-col items-center justify-center gap-1 text-ink-4 transition-colors duration-[160ms] group-hover/banner:text-brand">
                  <Icon className="size-6 fill-current" name={iconUpload || 'image'} />
                  <span className="text-[13px] tracking-[-0.2px]">
                    {btnText || 'Upload banner'}
                  </span>
                </span>
              )}

              <input
                id={inputId}
                type="file"
                accept={accept}
                onChange={handleChange}
                className="hidden"
                disabled={disabled}
                ref={ref}
              />
            </label>

            <div className="flex items-center justify-between gap-3">
              {note && <p className={cn(NOTE_CLASS, 'mt-0')}>{note}</p>}
              {value && (
                <button
                  type="button"
                  onClick={handleRemove}
                  className="ml-auto shrink-0 text-[12px] font-medium tracking-[-0.2px] text-ink-3 underline-offset-2 transition-colors duration-[140ms] hover:text-[#D33] hover:underline"
                >
                  Use default
                </button>
              )}
            </div>
          </div>

          <FieldError message={error?.message} />
        </div>
      )
    }

    // ── Row variant ────────────────────────────────────────────────────────
    if (variant === 'row') {
      const fileName = value instanceof File ? value.name : value
      return (
        <div className={cn('relative', className)}>
          <label
            className={cn(
              'flex h-8 cursor-pointer items-center gap-2 rounded-[8px] bg-surface px-2 text-[14px] tracking-[-0.2px] shadow-input transition-shadow duration-[160ms] ease-out-strong hover:shadow-[0_4px_4px_-3px_rgba(0,0,0,0.1),0_0_0_1px_rgb(var(--lc-line-2))]',
              disabled && 'cursor-not-allowed opacity-60',
              error?.message && INPUT_ERROR_CLASS,
              classInnerInput
            )}
          >
            <input
              type="file"
              accept={accept}
              onChange={handleChange}
              className="hidden"
              disabled={disabled}
              ref={ref}
            />
            <LcIcon name="attachment" className="shrink-0 text-ink-4" />
            <span className={cn('flex-1 truncate', fileName ? 'text-ink' : 'text-ink-4')}>
              {fileName || btnText || 'Choose file'}
            </span>
            {fileName ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  handleRemove()
                }}
                aria-label="Remove file"
                className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-ink-4 transition-colors hover:text-[#D33]"
              >
                <LcIcon name="close" size={12} />
              </button>
            ) : acceptHint ? (
              <span className="shrink-0 text-[12px] text-ink-4">{acceptHint}</span>
            ) : null}
          </label>
          <FieldError message={error?.message} />
        </div>
      )
    }

    // ── Default drop-zone variant ──────────────────────────────────────────
    return (
      <div className={cn('relative', className)}>
        {labelNode}

        <div
          className={cn(
            'relative rounded-[12px] border border-dashed border-line-2 bg-canvas p-6 text-center transition-colors duration-[160ms] hover:border-brand/60 dark:bg-surface-2',
            error?.message && 'border-[#D33]',
            classInnerInput
          )}
        >
          {value ? (
            <div className="flex flex-col items-center gap-2">
              {preview && isImage ? (
                <Image
                  src={preview}
                  alt="Preview"
                  width={64}
                  height={64}
                  className={cn('mx-auto size-16 rounded-[10px] object-contain', classImg)}
                />
              ) : (
                <div className="flex flex-col items-center">
                  <LcIcon name="file" size={28} className="text-ink-3" />
                  <p className="mt-1 max-w-full truncate text-[13px] text-ink">
                    {value instanceof File ? value.name : value}
                  </p>
                </div>
              )}
              <button
                type="button"
                onClick={handleRemove}
                className="text-[12px] font-medium text-[#D33] underline-offset-2 hover:underline"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <input
                type="file"
                accept={accept}
                onChange={handleChange}
                className="absolute inset-0 z-[1] cursor-pointer opacity-0"
                id={inputId}
                disabled={disabled}
                aria-label={label}
              />
              {iconUpload && <Icon className="size-6 fill-ink-4" name={iconUpload} />}
              <span
                className={cn(
                  'relative z-[2] inline-flex h-8 items-center rounded-[9px] bg-surface px-3 text-[13px] font-medium tracking-[-0.2px] text-ink-2 shadow-input',
                  classInput
                )}
              >
                {btnText || 'Choose file'}
              </span>
              {acceptHint && <span className="text-[12px] text-ink-4">{acceptHint}</span>}
            </div>
          )}
        </div>

        {note && <p className={NOTE_CLASS}>{note}</p>}
        <FieldError message={error?.message} />
        <input type="text" ref={ref} className="absolute -z-20 h-0 w-0 opacity-0" aria-label="" />
      </div>
    )
  }
)

FileField.displayName = 'FileField'

export default FileField
